import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Modal, renderShowConfirmationModal, onHideHandler, Loader } from '../..';
import { Field } from '../../Form';
import { useBeforeUnload } from '../../../hooks/useBeforeUnload/useBeforeUnload';
import { actions as photoSeriesActions, constants as photoSeriesConstants } from '../../../redux/photoSeries';
import { makeGet, makePut } from '../../../api/ajax';

const ProductInfoModal = ({ onModalClose, photoSeriesId, bodyPart, productInfo, productInfoStatus, ...props }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [showErrorMessage, setShowErrorMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoadingAnalyze, setIsLoadingAnalyze] = useState(false);

  const dispatch = useDispatch();

  useBeforeUnload(hasUnsavedChanges);

  const showErrorInfoStatus = productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.NOT_FOUND
    || productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.INVALID_VIN;

  const isInvalidVin = productInfoStatus === photoSeriesConstants.PRODUCT_INFO_STATUS.INVALID_VIN;

  const initialValues = {
    OeCode: productInfo?.OeCode || '',
    ArgicEuroCode: productInfo?.ArgicEuroCode || '',
    CalibrationNeeded: productInfo?.CalibrationNeeded || '',
  };

  const handleFindProductInfo = () => {
    setShowSuccessMessage(null);
    setShowErrorMessage(null);
    setIsLoadingAnalyze(true);
    makeGet(`photoseries/${photoSeriesId}/productInfo/${bodyPart}/analyze`).subscribe(
      ({ response }) => {
        dispatch(photoSeriesActions.updateRepairEstimationProductInfoSuccess(photoSeriesId, bodyPart, response));
        setIsLoadingAnalyze(false);
        onModalClose();
      },
      ({ response: { error } }) => {
        setIsLoadingAnalyze(false);
        setShowErrorMessage(error);
      },
    );
  };

  const handleSubmit = (data, setStatus) => {
    setShowErrorMessage(null);
    setShowSuccessMessage(null);

    // Create the request body with only non-empty fields
    const productInfo = {};
    if (data.OeCode) productInfo.OeCode = data.OeCode;
    if (data.ArgicEuroCode) productInfo.ArgicEuroCode = data.ArgicEuroCode;
    if (data.CalibrationNeeded) productInfo.CalibrationNeeded = data.CalibrationNeeded;

    makePut(`photoseries/${photoSeriesId}/productInfo/${bodyPart}`, { productInfo }).subscribe(
      ({ response }) => {
        // Update the product info for the specific body part in redux
        dispatch(photoSeriesActions.updateRepairEstimationProductInfoSuccess(photoSeriesId, bodyPart, response));
        setShowSuccessMessage('Product information has been updated!');
        setHasUnsavedChanges(false);
      },
      ({ response: { error } }) => {
        setStatus(error);
        setHasUnsavedChanges(false);
        setShowErrorMessage(error);
      },
    );
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, () => {
        onModalClose();
        setShowConfirmModal(false);
        setHasUnsavedChanges(false);
      })}
      <Modal
        size="md"
        onHide={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
        headerText="Edit Product Information"
        backdrop="static"
        {...props}
      >
        <Modal.Body>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <Alert variant="success" dismissible onClick={() => setShowSuccessMessage(null)}>
                  {showSuccessMessage}
                </Alert>
              )}
              {
                showErrorMessage && (
                  <Alert variant="danger" dismissible onClick={() => setShowErrorMessage(null)}>
                    {showErrorMessage}
                  </Alert>
                )
              }
              {
                showErrorInfoStatus && (
                  <Alert variant="warning" onClick={() => setShowSuccessMessage(null)}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>
                        <b>{photoSeriesConstants.PRODUCT_INFO_STATUS_DESCRIPTION[productInfoStatus]}</b>
                        {isInvalidVin && (
                          <>
                            <br /><br />
                            If you are <b>sure</b> this is a valid
                            <br />
                            <u>passenger car VIN</u>, trigger
                            <br />
                            product info request manually
                          </>
                        )}
                      </span>
                      {isInvalidVin && (
                        <button
                          type="button"
                          className="btn btn-primary d-flex align-items-center"
                          onClick={handleFindProductInfo}
                          disabled={isLoadingAnalyze}
                        >
                          {isLoadingAnalyze && (
                            <div className="position-relative mr-2" style={{ width: '1rem', height: '1rem' }}>
                              <Loader size="tiny" isRelative noDelay white />
                            </div>
                          )}
                          Find product info
                        </button>
                      )}
                    </div>
                  </Alert>
                )
              }
            </div>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              OeCode: Yup.string(),
              ArgicEuroCode: Yup.string(),
              CalibrationNeeded: Yup.string(),
            })}
            onSubmit={(values, { setStatus }) => handleSubmit(values, setStatus)}
            validateOnChange={false}
          >
            {({ values, errors, status, touched }) => {
              // Check if at least one field has a value
              const hasAtLeastOneField = values.OeCode || values.ArgicEuroCode || values.CalibrationNeeded;

              return (
                <Form
                  onChange={({ target }) => {
                    if (!hasUnsavedChanges && target.value !== initialValues[target.name]) {
                      setHasUnsavedChanges(true);
                    }
                  }}
                >
                  <div className="row mb-3">
                    <div className="col-12">
                      <label htmlFor="OeCode">OE Code</label>
                      <Field
                        type="text"
                        name="OeCode"
                        touched={touched.OeCode}
                        errors={errors.OeCode || (status && status.OeCode)}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12">
                      <label htmlFor="ArgicEuroCode">Argic Eurocode</label>
                      <Field
                        type="text"
                        name="ArgicEuroCode"
                        touched={touched.ArgicEuroCode}
                        errors={errors.ArgicEuroCode || (status && status.ArgicEuroCode)}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12">
                      <label htmlFor="CalibrationNeeded">Calibration needed</label>
                      <Field
                        type="text"
                        name="CalibrationNeeded"
                        touched={touched.CalibrationNeeded}
                        errors={errors.CalibrationNeeded || (status && status.CalibrationNeeded)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 text-right">
                      <button
                        type="button"
                        onClick={onModalClose}
                        className="btn btn-secondary mr-2"
                        disabled={isLoadingAnalyze}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!hasAtLeastOneField || isLoadingAnalyze}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

ProductInfoModal.propTypes = {
  onModalClose: PropTypes.func.isRequired,
  photoSeriesId: PropTypes.string.isRequired,
  bodyPart: PropTypes.number.isRequired,
  productInfo: PropTypes.shape({
    OeCode: PropTypes.string,
    ArgicEuroCode: PropTypes.string,
    CalibrationNeeded: PropTypes.string,
  }),
  productInfoStatus: PropTypes.number,
};

export default ProductInfoModal;
